import { redirect } from "@tanstack/react-router";
import posthog from "posthog-js";

// The key as registered in the PostHog dashboard — renaming it here without
// renaming it there turns the kill switch permanently off.
export const MBC_FLAG = "mbc";
export const SOCIAL_PUBLISHING_FLAG = "social-publishing";

let flagsLoadedPromise: Promise<void> | undefined;

/**
 * Resolves once PostHog has loaded feature flags for the current user.
 *
 * `posthog.onFeatureFlags` fires once on initial load and again on every
 * subsequent reload, so the listener is only used to resolve the promise on
 * its first invocation. The promise (and the underlying listener) is
 * memoized so multiple callers share a single subscription.
 */
export function waitForPostHogFlags(): Promise<void> {
  if (!flagsLoadedPromise) {
    flagsLoadedPromise = new Promise((resolve) => {
      posthog.onFeatureFlags(() => {
        resolve();
      });
    });
  }

  return flagsLoadedPromise;
}

/**
 * Router-level kill switch for the MBC http. Awaits PostHog
 * flag load, then throws a `redirect` if the flag is off (or unresolved),
 * preventing the route's component from ever mounting.
 *
 * Intended for use from a route's `beforeLoad`, e.g.:
 *
 * ```ts
 * export const Route = createFileRoute("/_auth/_app-shell/requests/mbc")({
 *   beforeLoad: requireMbcFeatureFlag,
 *   component: RouteComponent,
 * });
 * ```
 */
async function requireFeatureFlag(flag: string, fallback: string) {
  await waitForPostHogFlags();

  if (!posthog.isFeatureEnabled(flag)) {
    throw redirect({ to: fallback });
  }
}

export const requireMbcFeatureFlag = (): Promise<void> =>
  requireFeatureFlag(MBC_FLAG, "/requests/create");

export const requireSocialPublishingFeatureFlag = (): Promise<void> =>
  requireFeatureFlag(SOCIAL_PUBLISHING_FLAG, "/requests");
